import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  DialogContent,
  Dialog,
  DialogTitle,
  Snackbar,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import MuiAlert from "@mui/material/Alert";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { usePendingReceipt } from "../hooks/logic/usePendingReceipt";
import { isNotGeneralUser } from "../constants/roles";
import { PendingReceiptStyle as styles } from "./PendingReceiptStyle";
import SearchByGenre from "../components/SearchByGenre";
import UserFilterSelect from "../components/UserFilter";
import StatusSelect from "../components/StatusSelect";
import PendingReceiptTable, {
  PendingReceiptItem,
} from "../components/pendingReceipt/PendingReceiptTable";
import ProductInfoDialog from "../components/ProductInfoDialog";
import { useRecoilValue } from "recoil";
import { statusListSelector } from "../recoil/selectors/statusListSelector";
import ConfirmDialog from "../components/ConfirmDialog";

const PendingReceipt = () => {
  const {
    inputRef,
    loginUser,
    inputValue,
    handleInputChange,
    pendingReceiptList,
    userOptions,
    selectedUserId,
    handleUserChange,
    checkedIds,
    handleCheckChange,
    totalExpectedCount,
    searchGenres,
    selectedGenreId,
    setSelectedGenreId,
    focusInputField,
    handleGenreChange,
    searchKeyword,
    setSearchKeyword,
    searchItems,
    resetFilter,
    resetGenreWord,
    handleKeywordChange,
    expectedCount,
    handleChangeExpectedCount,
    handleRowClick,
    selectedStatusId,
    handleStatusChange,
    handleClickOkButton,
    handleUpdateAndPrint,
    detailDialogOpen,
    setDetailDialogOpen,
    selectedParent,
    setSelectedParent,
    selectedChildren,
    notionConfirmDialog,
    setNotionConfirmDialog,
    multiItemConfirmDialog,
    setMultiItemConfirmDialog,
    printConfirmDialog,
    setPrintConfirmDialog,
    cautionDialog,
    setCautionDialog,
    cautionMessage,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    handleMultiUpdateStatus,
    handleExpiryDatePrintAllAtOnce,
    handlePrintAllAtOnce,
    handleDeleteAllAtOnce,
    setSortKey,
    setSortOrder,
    handlePageChange,
    page,
    totalListCount,
    changeSortKey,
  } = usePendingReceipt();

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const statusList = useRecoilValue(statusListSelector);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <ProductInfoDialog
        open={detailDialogOpen}
        onClose={() => {
          setSelectedParent(null);
          selectedChildren.current = [];
          setDetailDialogOpen(false);
        }}
        title="商品情報"
        parentItem={selectedParent!}
        childItems={selectedChildren.current}
        statusId={selectedParent?.statusId ?? 0}
      />
      <ConfirmDialog
        open={notionConfirmDialog}
        title="確認"
        message="予定総数と入荷総数が異なります。更新可能な商品のみ更新しますか？"
        buttons={[
          {
            label: "いいえ",
            variant: "outlined",
            onClick: () => setNotionConfirmDialog(false),
          },
          {
            label: "はい",
            variant: "contained",
            onClick: () => {
              setNotionConfirmDialog(false);
              setPrintConfirmDialog(true);
            },
          },
        ]}
      />
      <ConfirmDialog
        open={multiItemConfirmDialog}
        title="確認"
        message="複数商品が更新対象となっています。更新しますか？"
        buttons={[
          {
            label: "いいえ",
            variant: "outlined",
            onClick: () => setMultiItemConfirmDialog(false),
          },
          {
            label: "はい",
            variant: "contained",
            onClick: () => {
              setMultiItemConfirmDialog(false);
              handleMultiUpdateStatus();
            },
          },
        ]}
      />
      <ConfirmDialog
        open={printConfirmDialog}
        title="確認"
        message="ステータス更新時にラベル印刷しますか？"
        buttons={[
          {
            label: "キャンセル",
            variant: "outlined",
            onClick: () => setPrintConfirmDialog(false),
          },
          {
            label: "更新のみ",
            variant: "contained",
            onClick: () => {
              setPrintConfirmDialog(false);
              handleUpdateAndPrint(false);
            },
          },
          {
            label: "印刷する",
            variant: "contained",
            onClick: () => {
              setPrintConfirmDialog(false);
              // handleUpdateAndPrint();
              handleUpdateAndPrint(true);
            },
          },
        ]}
      />
      <Box sx={styles.titleBar}>
        <Typography variant="h6" sx={styles.titleText}>
          入荷待ち
        </Typography>
      </Box>
      <Box py={0} px={0}>
        {/* 1段目 フィルター群 */}
        <Box
          display="flex"
          gap={2}
          mb={2}
          px={1}
          flexWrap="wrap"
          alignItems="center"
        >
          {isNotGeneralUser(loginUser?.permissionId) && (
            <UserFilterSelect
              options={userOptions}
              onSelect={handleUserChange}
              value={selectedUserId}
              sx={{ width: "300px" }}
            />
          )}
          <Button
            variant="outlined"
            onClick={async () => {
              await resetFilter();
              // searchItems("");
            }}
          >
            フィルター解除
          </Button>

          {/* 🔽 ボタン用の右寄せBox */}
          <Box display="flex" gap={1} ml="auto">
            {isNotGeneralUser(loginUser?.permissionId) && (
              <DatePicker
                label="賞味期限"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                minDate={new Date()}
                format="yyyy/MM/dd"
                slotProps={{
                  textField: {
                    size: "small",
                    inputProps: {
                      readOnly: false,
                    },
                  },
                }}
                sx={{ width: "150px" }}
              />
            )}
            <Box display="flex" alignItems="center" gap={2}>
              {isNotGeneralUser(loginUser?.permissionId) && (
                <TextField
                  placeholder={"入荷総数"}
                  size="small"
                  inputRef={inputRef}
                  type="number"
                  value={expectedCount}
                  onChange={handleChangeExpectedCount}
                  sx={styles.numberField}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (totalExpectedCount > 0 && expectedCount !== "")
                        handleClickOkButton();
                    }
                  }}
                />
              )}
              <Box flexGrow={1}>
                <Typography>予定総数: {totalExpectedCount} 個</Typography>
              </Box>
            </Box>
            {isNotGeneralUser(loginUser?.permissionId) && (
              <>
                <StatusSelect
                  options={
                    statusList?.filter((d) => d.id === 2 || d.id === 3) || []
                  }
                  value={selectedStatusId ?? 2}
                  initialValue={2} // 初期値を「梱包待ち」にする
                  onChange={handleStatusChange}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    handleClickOkButton();
                  }}
                  disabled={totalExpectedCount === 0}
                >
                  OK
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* 2段目 検索フィルター */}
        <Box display="flex" gap={2} mb={2} px={1} flexWrap="wrap">
          <SearchByGenre
            options={["JAN", "ASIN", "商品名", "FNSKU", "SKU"]}
            defaultGenre="JAN"
            onGenreChange={handleGenreChange}
            searchKeyword={searchKeyword}
            onKeywordChange={setSearchKeyword}
            autoFocus={true}
            onEnter={() => {
              searchItems(searchKeyword);
              focusInputField();
            }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              searchItems(searchKeyword);
            }}
          >
            検索
          </Button>
        </Box>

        {/* 3段目 商品一覧テーブル */}
        <Box mt={2}>
          <PendingReceiptTable
            data={pendingReceiptList}
            totalListCount={totalListCount}
            checkedIds={checkedIds}
            onCheckedChange={handleCheckChange}
            onParentRowClick={handleRowClick}
            handleExpiryDatePrintAllAtOnce={handleExpiryDatePrintAllAtOnce}
            handlePrintAllAtOnce={handlePrintAllAtOnce}
            handleDeleteAllAtOnce={handleDeleteAllAtOnce}
            onSortChange={(key, ord) => {
              setSortKey(key);
              setSortOrder(ord);
              changeSortKey(key, ord);
            }}
            onPageChange={handlePageChange}
            page={page}
            loginUser={loginUser}
          ></PendingReceiptTable>
        </Box>
      </Box>
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={handleCloseToast}
          severity={toastSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {toastMessage}
        </MuiAlert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default PendingReceipt;
